import React, { useEffect, useMemo, useState } from "react";

import { getEventOwnerLine } from "../utils/ownerLine";

const CAMERA_PAGE_SIZE = 8;

export default function SettingsPage({ me, permissionFlags }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // 민트워치 사용 권한(=메시지 권한)이 있는 계정만 민트워치 연결 QR을 볼 수 있다.
  const canMintwatch = !!permissionFlags?.canMessages;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [myCameras, setMyCameras] = useState([]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState("");

  // 우측 상세: 영상 QR 표시 여부, 워치 QR 데이터
  const [showRtspQr, setShowRtspQr] = useState(false);
  const [watchQr, setWatchQr] = useState(null);
  const [watchQrLoading, setWatchQrLoading] = useState(false);
  const [watchQrError, setWatchQrError] = useState("");

  // 우측 상세: 번호/이름 편집
  const [editing, setEditing] = useState(false);
  const [editCamNo, setEditCamNo] = useState("");
  const [editCamTitle, setEditCamTitle] = useState("");
  const [cameraSaving, setCameraSaving] = useState(false);

  const camKeyOf = (cam, idx = 0) => String(cam?.camIdx || cam?.camNo || idx);

  const loadMyCameras = async () => {
    try {
      setCameraLoading(true);
      setCameraError("");
      const res = await fetch("/api/my-cameras", { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data?.success || !Array.isArray(data?.cameras)) {
        throw new Error(
          data?.message || data?.error || "카메라 정보를 불러오지 못했습니다."
        );
      }
      setMyCameras(data.cameras);
    } catch (err) {
      setMyCameras([]);
      setCameraError(err.message || "카메라 정보를 불러오지 못했습니다.");
    } finally {
      setCameraLoading(false);
    }
  };

  useEffect(() => {
    loadMyCameras();
  }, []);

  const filteredCameras = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return myCameras;
    return myCameras.filter((cam) => {
      const name = String(cam?.name || cam?.camName || "").toLowerCase();
      const no = String(cam?.camNo ?? "").toLowerCase();
      const key = String(cam?.streamKey || "").toLowerCase();
      return name.includes(q) || no.includes(q) || key.includes(q);
    });
  }, [myCameras, search]);

  const pageCount = Math.max(1, Math.ceil(filteredCameras.length / CAMERA_PAGE_SIZE));

  const pagedCameras = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), pageCount);
    const start = (safePage - 1) * CAMERA_PAGE_SIZE;
    return filteredCameras.slice(start, start + CAMERA_PAGE_SIZE);
  }, [filteredCameras, page, pageCount]);

  const selectedCam = useMemo(
    () => myCameras.find((c, i) => camKeyOf(c, i) === selectedKey) || null,
    [myCameras, selectedKey]
  );

  // 첫 진입/검색 시 선택이 비어있으면 첫 카메라 자동 선택
  useEffect(() => {
    if (filteredCameras.length === 0) {
      if (selectedKey) setSelectedKey("");
      return;
    }
    const stillVisible = filteredCameras.some((c, i) => camKeyOf(c, i) === selectedKey);
    if (!stillVisible) {
      setSelectedKey(camKeyOf(filteredCameras[0]));
    }
  }, [filteredCameras]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택 카메라가 바뀌면 우측 상태 초기화
  useEffect(() => {
    setShowRtspQr(false);
    setWatchQr(null);
    setWatchQrError("");
    setEditing(false);
    setEditCamNo(String(selectedCam?.camNo || ""));
    setEditCamTitle(String(selectedCam?.name || selectedCam?.camName || ""));
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("모든 항목을 입력해주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    if (newPassword.length < 6) {
      setError("새 비밀번호는 최소 6자 이상이어야 합니다.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("현재 비밀번호와 새 비밀번호가 같습니다.");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch("/api/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "비밀번호 변경에 실패했습니다.");
      setMessage(data.message || "비밀번호가 변경되었습니다.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message || "비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, label = "복사") => {
    if (!text || text === "-") return;
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} 완료`);
    } catch (err) {
      alert(`${label} 실패`);
    }
  };

  const handleCreateWatchQr = async (cam) => {
    const camIdx = Number(cam?.camIdx || 0);
    if (!camIdx) {
      alert("카메라 정보를 확인할 수 없습니다.");
      return;
    }
    try {
      setWatchQrLoading(true);
      setWatchQrError("");
      const res = await fetch("/api/watch-pair/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ camIdx }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.pairCode || !data?.qrPayload) {
        throw new Error(
          data?.message || data?.error || "워치 연결 QR 생성에 실패했습니다."
        );
      }
      setWatchQr(data);
    } catch (err) {
      setWatchQrError(err.message || "워치 연결 QR 생성에 실패했습니다.");
    } finally {
      setWatchQrLoading(false);
    }
  };

  const handleSaveCameraInfo = async (cam) => {
    const camIdx = Number(cam?.camIdx || 0);
    const currentCamNo = Number(cam?.camNo || 0);
    const nextCamNo = Number(editCamNo || 0);
    const camTitle = String(editCamTitle || "").trim();
    if (!nextCamNo || !Number.isInteger(nextCamNo) || nextCamNo < 1) {
      alert("카메라 번호는 1 이상의 숫자여야 합니다.");
      return;
    }
    if (!camTitle) {
      alert("카메라 이름을 입력해주세요.");
      return;
    }
    try {
      setCameraSaving(true);
      const res = await fetch("/api/my-camera/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ camIdx, currentCamNo, nextCamNo, camTitle }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "카메라 정보 수정 실패");
        return;
      }
      alert("카메라 정보 수정 완료");
      setEditing(false);
      await loadMyCameras();
    } catch (err) {
      alert("카메라 정보 수정 중 오류가 발생했습니다.");
    } finally {
      setCameraSaving(false);
    }
  };

  const formatExpiresAt = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString("ko-KR");
    } catch (err) {
      return String(value);
    }
  };

  const rtspUrl = selectedCam?.rtspUrl || "-";
  const rtspQrUrl =
    rtspUrl && rtspUrl !== "-"
      ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
          rtspUrl
        )}`
      : "";
  const watchQrUrl = watchQr?.qrPayload
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
        watchQr.qrPayload
      )}`
    : "";

  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1">
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">설정</h2>

        {/* 비밀번호 변경 (상단) */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          <h3 className="mb-3 text-base font-bold text-gray-900 dark:text-white">
            비밀번호 변경
          </h3>
          <form
            onSubmit={handleChangePassword}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="현재 비밀번호"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white sm:w-44"
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="새 비밀번호"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white sm:w-44"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="새 비밀번호 확인"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white sm:w-44"
            />
            <button
              type="submit"
              disabled={loading}
              className="shrink-0 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              {loading ? "변경 중..." : "변경"}
            </button>
            {message && (
              <span className="text-sm text-green-600 dark:text-green-300">{message}</span>
            )}
            {error && (
              <span className="text-sm text-red-600 dark:text-red-300">{error}</span>
            )}
          </form>
        </section>

        {/* 카메라 / 스트림 — 좌측 목록 + 우측 상세 */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          <h3 className="mb-4 text-base font-bold text-gray-900 dark:text-white">
            카메라 / 스트림
          </h3>

          {cameraError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {cameraError}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:min-h-[460px] lg:grid-cols-12">
              {/* 좌측 목록 */}
              <div className="lg:col-span-4">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="카메라 이름 / 번호 검색"
                  className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />

                <div className="space-y-1.5">
                  {cameraLoading ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                      불러오는 중...
                    </div>
                  ) : filteredCameras.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                      {search ? "검색 결과가 없습니다." : "등록된 카메라가 없습니다."}
                    </div>
                  ) : (
                    pagedCameras.map((cam, idx) => {
                      const key = camKeyOf(cam, idx);
                      const active = key === selectedKey;
                      return (
                        <div
                          key={key}
                          className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 transition ${
                            active
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedKey(key)}
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-700 dark:bg-white/10 dark:text-gray-200">
                              {cam.camNo || "-"}
                            </span>
                            <span className="line-clamp-1 text-sm font-medium text-gray-900 dark:text-white">
                              {cam.name || cam.camName || "-"}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedKey(key);
                              setEditing(true);
                            }}
                            className="shrink-0 rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
                          >
                            수정
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                {filteredCameras.length > CAMERA_PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      ◀ 이전
                    </button>
                    <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {page} / {pageCount}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      다음 ▶
                    </button>
                  </div>
                )}
              </div>

              {/* 우측 상세 */}
              <div className="lg:col-span-8">
                {!selectedCam ? (
                  <div className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-gray-600">
                    왼쪽에서 카메라를 선택하세요.
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700 lg:h-full lg:min-h-[420px]">
                    {/* 헤더: 제목 */}
                    <div className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                      {/* "지점 / 팀 / 이름 / 차량번호".
                          값이 하나도 없으면 기존 표기로 돌아간다.
                          편집 중인 camNo·camTitle 의 실제 값은 아래 편집 폼
                          입력칸에 그대로 보이므로 여기서 빠져도 확인할 수 있다. */}
                      {getEventOwnerLine(selectedCam) ||
                        `CAM ${selectedCam.camNo || "-"} · ${
                          selectedCam.name || selectedCam.camName || "카메라"
                        }`}
                    </div>

                    {/* 편집 모드 */}
                    {editing && (
                      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-[#16202C]">
                        <label className="text-xs text-gray-600 dark:text-gray-300">
                          번호
                          <input
                            type="number"
                            min="1"
                            value={editCamNo}
                            onChange={(e) => setEditCamNo(e.target.value)}
                            className="mt-1 block w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                          />
                        </label>
                        <label className="flex-1 text-xs text-gray-600 dark:text-gray-300">
                          이름
                          <input
                            type="text"
                            value={editCamTitle}
                            onChange={(e) => setEditCamTitle(e.target.value)}
                            className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => handleSaveCameraInfo(selectedCam)}
                          disabled={cameraSaving}
                          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {cameraSaving ? "저장 중..." : "저장"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(false)}
                          className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-gray-200"
                        >
                          취소
                        </button>
                      </div>
                    )}

                    {/* 정보 */}
                    <div className="space-y-2">
                      <InfoRow
                        label="스트림 키"
                        value={selectedCam.streamKey || "-"}
                        onCopy={() => handleCopy(selectedCam.streamKey, "스트림 키 복사")}
                      />
                      <InfoRow
                        label="RTSP 주소"
                        value={rtspUrl}
                        onCopy={() => handleCopy(rtspUrl, "RTSP 주소 복사")}
                      />

                      {/* 영상 연결 QR — RTSP 바로 아래 */}
                      <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            영상 연결 QR
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowRtspQr((v) => !v)}
                            className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-blue-600 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-blue-300"
                          >
                            {showRtspQr ? "숨기기" : "QR 보기"}
                          </button>
                        </div>
                        {showRtspQr && (
                          <div className="mt-3">
                            {rtspQrUrl ? (
                              <img
                                src={rtspQrUrl}
                                alt="RTSP QR"
                                width={180}
                                height={180}
                                className="rounded-lg border border-gray-200 bg-white p-2"
                              />
                            ) : (
                              <div className="text-xs text-gray-500">RTSP 주소가 없습니다.</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 민트워치 연결 QR — 권한 있는 계정만 */}
                      {canMintwatch && (
                        <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                              민트워치 연결 QR
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCreateWatchQr(selectedCam)}
                              disabled={watchQrLoading}
                              className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-emerald-600 shadow-sm hover:bg-gray-100 disabled:opacity-60 dark:bg-white/10 dark:text-emerald-300"
                            >
                              {watchQrLoading
                                ? "생성 중..."
                                : watchQr
                                ? "재생성"
                                : "QR 생성"}
                            </button>
                          </div>
                          {watchQrError && (
                            <div className="mt-2 max-w-[260px] text-xs text-red-600 dark:text-red-300">
                              {watchQrError}
                            </div>
                          )}
                          {watchQr?.qrPayload && (
                            <div className="mt-3">
                              <img
                                src={watchQrUrl}
                                alt="MintWatch QR"
                                width={190}
                                height={190}
                                className="rounded-lg border border-gray-200 bg-white p-2"
                              />
                              <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                                유효 {watchQr.expiresInMinutes || 10}분 · 만료{" "}
                                {formatExpiresAt(watchQr.expiresAt)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function InfoRow({ label, value, onCopy }) {
  const has = value && value !== "-";
  return (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]">
      <div className="w-24 shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="min-w-0 flex-1 break-all text-sm text-gray-800 dark:text-gray-100">
        {value}
      </div>
      {has && (
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-100 dark:bg-white/10 dark:text-gray-200"
        >
          복사
        </button>
      )}
    </div>
  );
}
